import React, { useState, useEffect } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { Bounce } from 'react-toastify';
import { toast } from '../utils/customToast';
import OAuthButtons from '../components/shared/OAuthButtons';
import 'react-toastify/dist/ReactToastify.css';
import usePageTitle from '../hooks/usePageTitle';

const Login = () => {
    usePageTitle('Connexion');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isRegister, setIsRegister] = useState(false);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();

    // Charger l'email sauvegardé au montage
    useEffect(() => {
        const savedEmail = localStorage.getItem('lastEmail');
        if (savedEmail) {
            setEmail(savedEmail);
        }
    }, []);

    // Détecter le mode sombre système au chargement
    useEffect(() => {
        const checkDarkMode = () => {
            const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (systemPrefersDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
        };

        checkDarkMode();

        // Écouter les changements du thème système
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => checkDarkMode();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        } else {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, []);

    const isValidEmail = (em) => {
        if (!em || typeof em !== 'string') return false;
        const re = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        return re.test(String(em).trim().toLowerCase());
    };

    const isValidName = (n) => {
        if (!n || typeof n !== 'string') return false;
        const len = String(n).trim().length;
        return len >= 2 && len <= 30;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const endpoint = isRegister ? '/auth/register' : '/auth/login';
        const body = isRegister ? { email, password, name } : { email, password };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (response.ok) {
                // Sauvegarder l'email en cas de connexion réussie
                localStorage.setItem('lastEmail', email);

                login(data.token, data.user);
                toast.success(isRegister ? 'Compte créé avec succès !' : 'Connexion réussie !', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            } else {
                let msg = data.message || 'Erreur de connexion';
                if (data.remainingAttempts !== undefined && data.remainingAttempts > 0) {
                    msg += ` (${data.remainingAttempts} essai${data.remainingAttempts > 1 ? 's' : ''} restant${data.remainingAttempts > 1 ? 's' : ''})`;
                }
                toast.error(msg, {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            }
        } catch (error) {
            toast.error('Erreur réseau. Vérifiez votre connexion.', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setLoading(false);
        }
    };

    // Social login placeholders removed to avoid unused variables in build.

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
            <div className="w-full mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch min-h-[60vh] md:min-h-screen">
                    {/* Left: form */}
                    <div className="p-8 rounded-lg shadow-lg w-full h-full flex items-center">
                        <div className="w-full max-w-md mx-auto">
                            <h2 className="text-2xl font-bold text-center mb-6 text-black dark:text-white">
                                {isRegister ? 'Créer un compte' : 'Connexion'}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {isRegister && (
                                    <>
                                        <input
                                            type="text"
                                            placeholder="Votre prénom"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                            required
                                            disabled={loading}
                                            maxLength={30}
                                        />
                                            <div className="text-xs text-gray-500 pt-1">{String(name).trim().length}/30</div>
                                    </>
                                )}

                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                    required
                                    disabled={loading}
                                    pattern="[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$"
                                    title="Entrez une adresse email valide (ex: utilisateur@domaine.com)"
                                />

                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Mot de passe"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                        required
                                        disabled={loading}
                                        aria-label="Mot de passe"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(s => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    >
                                        {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || (isRegister && (!isValidName(name) || !isValidEmail(email) || password.length < 8)) || (!isRegister && !isValidEmail(email))}
                                    className="w-full bg-primary text-white dark:text-black p-3 rounded hover:bg-primary-hover transition disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Chargement...' : (isRegister ? 'S\'inscrire' : 'Se connecter')}
                                </button>
                            </form>

                            {/* Séparateur */}
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">ou</span>
                                </div>
                            </div>

                            {/* Boutons OAuth */}
                            <OAuthButtons />

                            {!isRegister && (
                                <div className="text-center mt-3">
                                    <a
                                        href="/forgot-password"
                                        className="text-sm text-primary hover:underline dark:text-accent"
                                    >
                                        Mot de passe oublié ?
                                    </a>
                                </div>
                            )}

                            <div className="mt-4">
                                <button
                                    onClick={() => setIsRegister(!isRegister)}
                                    disabled={loading}
                                    className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white disabled:text-gray-400 dark:disabled:text-gray-600"
                                >
                                    {isRegister ? 'Déjà un compte ? Se connecter' : 'Pas de compte ? S\'inscrire'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right: decorative image / illustration - hidden on small screens */}
                    <div className="hidden md:flex items-center justify-center overflow-hidden h-full">
                        <div className="w-full h-full bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-center p-6">
                            {/* Simple SVG illustration to avoid external assets */}
                            <div className="max-w-xs text-white">
                                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-4" width="160" height="160" aria-hidden>
                                    <rect width="100%" height="100%" rx="20" fill="rgba(255,255,255,0.06)" />
                                    <g fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M40 130c10-15 30-25 60-25s50 10 60 25" opacity="0.6" />
                                        <rect x="45" y="45" width="110" height="60" rx="8" opacity="0.9" />
                                        <path d="M60 70h80" />
                                        <path d="M60 85h50" />
                                        <circle cx="150" cy="50" r="12" opacity="0.9" />
                                    </g>
                                </svg>

                                <h3 className="text-lg font-bold">Messagerie d'équipe</h3>
                                <p className="text-sm mt-2 opacity-90">Centralisez vos messages, rappels et tâches. Simple, rapide et sécurisé.</p>
                            </div>
                        </div>
                    </div>
                </div>


            </div>
        </div>
    );
};

export default Login;